import { useSyncExternalStore } from "react";

import type { DragKind, DraggedDocument } from "@/agent/chat";

import { applyLipSize } from "./lipSize";

/**
 * The lip's UI state, for this tab: whether its card is open, and what is being dragged over the
 * page. Outside Redux: it is UI state only, and anything may open the lip (a link, an "add to
 * chat") without being passed a callback.
 */
export type LipDrag = { kind: DragKind; overLip: boolean } | null;

/**
 * What the lip shows (its data-state). A drag over the page outlines the resting lip; over the lip
 * it rises to its drop area. An open card wins: its chat has its own drop area.
 */
export type LipState = "peek" | "page-drag" | "over" | "open";

export function lipStateOf(open: boolean, drag: LipDrag): LipState {
  if (open) return "open";
  if (!drag) return "peek";
  return drag.overLip ? "over" : "page-drag";
}

let open = false;
let drag: LipDrag = null;
/** A choice to bring into view once the lip's thread shows it (`?choice=` on the page's link). */
let choice: string | null = null;
/** A document a page's "Add to chat" handed over, for the lip's chat to attach (LipAddDocument). */
let document: DraggedDocument | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export function setLipOpen(next: boolean): void {
  if (next === open) return;
  open = next;
  // Opens at the chosen height, whatever a drag that closed it left on the variable.
  if (open) applyLipSize();
  notify();
}

/** Open the lip on a choice's card: the lip scrolls to it and focuses it once it is there. */
export function openLipOn(ref: string): void {
  choice = ref;
  setLipOpen(true);
  notify();
}

/** Open the lip and add an application's document to its message, as dropping it there does. */
export function addToLip(next: DraggedDocument): void {
  document = next;
  setLipOpen(true);
  notify();
}

/** The lip's chat took the document. */
export function clearLipDocument(): void {
  if (document === null) return;
  document = null;
  notify();
}

/** The card was focused, or the thread does not show it: the lip is simply open. */
export function clearLipChoice(): void {
  if (choice === null) return;
  choice = null;
  notify();
}

/** Called on every dragover; it notifies only when the drag starts, ends, or crosses the lip. */
export function setLipDrag(next: LipDrag): void {
  if (next?.kind === drag?.kind && next?.overLip === drag?.overLip) return;
  drag = next;
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLipOpen(): boolean {
  return useSyncExternalStore(subscribe, () => open);
}

/** The lip's state alone, so the dragovers never re-render the whole lip. */
export function useLipState(): LipState {
  return useSyncExternalStore(subscribe, () => lipStateOf(open, drag));
}

export function useLipChoice(): string | null {
  return useSyncExternalStore(subscribe, () => choice);
}

export function useLipDocument(): DraggedDocument | null {
  return useSyncExternalStore(subscribe, () => document);
}

export function useLipDrag(): LipDrag {
  return useSyncExternalStore(subscribe, () => drag);
}
